import { ChevronDownIcon, InfoCircleIcon } from '@langchain/untitled-ui-icons';

import { ReactNode, useState } from 'react';

import { Rate } from '@/components/Rate';
import { Skeleton } from '@/components/Skeleton';
import { Tooltip } from '@/design-system';
import { RunGroupStatsSchema, RunStatsSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { FeedbackChips } from '../../components/FeedbackChips';
import { SessionLatencyCell } from '../../components/SessionsTable/components/Cell';
import { RawTokensTooltip } from '../Run/components/TokensChip';
import { ProjectSidebarHeaderTag } from './ProjectSidebarComponents';
import { SIDEBAR_HEADER_STYLE } from './constants';

const formatter = new Intl.NumberFormat('en-US', {});
const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const STAT_TITLE_STYLE =
  'whitespace-nowrap text-xs font-medium tracking-tight text-quaternary';

/* ───────────────────────── StatItem ───────────────────────── */
interface StatItemProps<T> {
  label: React.ReactNode;
  value?: T;
  isLoading?: boolean;
  renderItem: (v: T) => React.ReactNode;
}

function StatItem<T>({
  label,
  value,
  isLoading,
  renderItem,
}: StatItemProps<T>) {
  const hasValue =
    value != null && (Array.isArray(value) ? value.length > 0 : true);

  if (!isLoading && !hasValue) return null; // hide completely

  return (
    <div className="flex flex-col gap-1">
      <div className={STAT_TITLE_STYLE}>{label}</div>
      <div className="font-medium">
        {isLoading ? <Skeleton className="m-0 h-5 w-16" /> : renderItem(value!)}
      </div>
    </div>
  );
}
/* ──────────────────────────────────────────────────────────── */

export function ProjectStatsV2({
  stats,
  tokenStats,
  feedbackStats,
  isLoadingStats,
  isLoadingTokenStats,
  isLoadingFeedbackStats,
  isTraces,
  useExperimentalSearch,
  source,
}: {
  stats?: RunStatsSchema | RunGroupStatsSchema;
  tokenStats?: RunStatsSchema;
  feedbackStats?: RunStatsSchema;
  additionalStats?: Array<{ label: ReactNode; value: ReactNode }>;
  isLoadingStats?: boolean;
  isLoadingTokenStats?: boolean;
  isLoadingFeedbackStats?: boolean;
  isTraces?: boolean;
  useExperimentalSearch?: boolean;
  source: 'conversation' | 'trace';
}) {
  const [statsOpen, setStatsOpen] = useState(true);

  const tokenStatsToUse = tokenStats ?? stats;
  const loadingTokenStats = isLoadingTokenStats ?? isLoadingStats;
  const feedbackStatsToUse = feedbackStats ?? stats;
  const loadingFeedbackStats = isLoadingFeedbackStats ?? isLoadingStats;

  return (
    <div
      className={cn('flex flex-col gap-1 text-sm', {
        'pb-4': statsOpen,
      })}
    >
      <div
        className={cn(SIDEBAR_HEADER_STYLE, 'relative')}
        onClick={() => setStatsOpen(!statsOpen)}
      >
        <div className="flex items-center gap-1">
          <h2>Stats</h2>
          {useExperimentalSearch && (
            <div className="flex items-center gap-1 text-xs text-warning">
              <Tooltip
                title={
                  <span className="truncate">
                    Stats don't update with experimental search
                  </span>
                }
              >
                <div>
                  <InfoCircleIcon className="size-4" />
                </div>
              </Tooltip>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          {stats && (
            <ProjectSidebarHeaderTag
              show={!statsOpen}
              text={
                source === 'conversation' && 'group_count' in stats
                  ? `${formatter.format(stats.group_count ?? 0)} threads`
                  : `${formatter.format(stats.run_count ?? 0)} runs`
              }
            />
          )}
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${
              statsOpen ? '' : '-rotate-90'
            }`}
          />
        </div>
      </div>

      <div className={cn(statsOpen ? 'flex flex-col gap-4 px-4' : 'hidden')}>
        {/* Thread-/Group-count */}
        {stats && 'group_count' in stats && stats.group_count != null && (
          <StatItem
            label="Thread Count"
            value={stats.group_count}
            isLoading={isLoadingStats}
            renderItem={(v) => (
              <span data-testid="project-stats-group-count">
                {formatter.format(v as number)}
              </span>
            )}
          />
        )}

        {/* Run / Trace count */}
        <StatItem
          label={isTraces ? 'Trace Count' : 'Run Count'}
          value={stats?.run_count}
          isLoading={isLoadingStats}
          renderItem={(v) => (
            <span data-testid="project-stats-run-count">
              {formatter.format(v as number)}
            </span>
          )}
        />

        {/* Total tokens */}
        <RawTokensTooltip
          promptCostDetails={tokenStatsToUse?.prompt_cost_details}
          completionCostDetails={tokenStatsToUse?.completion_cost_details}
          promptTokenDetails={tokenStatsToUse?.prompt_token_details}
          completionTokenDetails={tokenStatsToUse?.completion_token_details}
          totalTokens={tokenStatsToUse?.total_tokens}
          promptCost={tokenStatsToUse?.prompt_cost}
          completionCost={tokenStatsToUse?.completion_cost}
          promptTokens={tokenStatsToUse?.prompt_tokens}
          completionTokens={tokenStatsToUse?.completion_tokens}
          side="bottom"
        >
          <div>
            <StatItem
              label="Total Tokens"
              value={tokenStatsToUse?.total_tokens}
              isLoading={loadingTokenStats}
              renderItem={(v) => (
                <>
                  {formatter.format(v as number)}
                  {tokenStatsToUse?.total_cost != null && (
                    <span className="text-tertiary">
                      {' / '}
                      {usdFormatter.format(+tokenStatsToUse.total_cost)}{' '}
                    </span>
                  )}
                </>
              )}
            />
          </div>
        </RawTokensTooltip>

        {/* Median tokens */}
        <StatItem
          label="Median Tokens"
          value={tokenStatsToUse?.median_tokens}
          isLoading={loadingTokenStats}
          renderItem={(v) => formatter.format(v as number)}
        />

        {/* Error rate */}
        <StatItem
          label="Error Rate"
          value={stats?.error_rate}
          isLoading={isLoadingStats}
          renderItem={(v) => (
            <span data-testid="project-stats-error-rate">
              <Rate value={v as number} />
            </span>
          )}
        />

        {/* % Streaming */}
        <StatItem
          label="% Streaming"
          value={tokenStatsToUse?.streaming_rate}
          isLoading={loadingTokenStats}
          renderItem={(v) =>
            `${formatter.format(Math.round((v as number) * 100))}%`
          }
        />

        {/* First token latency */}
        <StatItem
          label="First Token"
          value={
            tokenStatsToUse?.first_token_p50 ?? tokenStatsToUse?.first_token_p99
          }
          isLoading={loadingTokenStats}
          renderItem={() =>
            tokenStatsToUse && (
              <div className="flex flex-wrap gap-1.5">
                <SessionLatencyCell
                  row={tokenStatsToUse}
                  metric="first_token_p50"
                  label="P50: "
                  labelPlacement="outside"
                  sessionStats={{
                    first_token_p50: tokenStatsToUse?.first_token_p50,
                    first_token_p99: tokenStatsToUse?.first_token_p99,
                  }}
                />
                <SessionLatencyCell
                  row={tokenStatsToUse}
                  metric="first_token_p99"
                  label="P99: "
                  labelPlacement="outside"
                  sessionStats={{
                    first_token_p50: tokenStatsToUse?.first_token_p50,
                    first_token_p99: tokenStatsToUse?.first_token_p99,
                  }}
                />
              </div>
            )
          }
        />

        {/* Overall latency */}
        <StatItem
          label="Latency"
          value={stats?.latency_p50 ?? stats?.latency_p99}
          isLoading={isLoadingStats}
          renderItem={() =>
            stats && (
              <div className="flex flex-wrap gap-1.5">
                <SessionLatencyCell
                  testId="project-stats-latency-p50"
                  row={stats}
                  label="P50: "
                  labelPlacement="outside"
                  metric="latency_p50"
                  sessionStats={{
                    latency_p50: stats?.latency_p50,
                    latency_p99: stats?.latency_p99,
                  }}
                />
                <SessionLatencyCell
                  testId="project-stats-latency-p99"
                  row={stats}
                  metric="latency_p99"
                  label="P99: "
                  labelPlacement="outside"
                  sessionStats={{
                    latency_p50: stats?.latency_p50,
                    latency_p99: stats?.latency_p99,
                  }}
                />
              </div>
            )
          }
        />

        {/* Feedback */}
        <StatItem
          label="Feedback"
          value={feedbackStatsToUse?.feedback_stats}
          isLoading={loadingFeedbackStats}
          renderItem={(feedbackStats) => (
            <div className="flex flex-wrap items-center gap-2">
              <FeedbackChips
                className="text-xxs"
                chipClassName="text-xxs"
                testIdPrefix="project-stats"
                feedbackStats={feedbackStats}
                disablePopover
              />
            </div>
          )}
        />
      </div>
    </div>
  );
}
